import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../auth/useAuth";
import {
  createWeatherWatch,
  deleteWeatherWatch,
  fetchWeatherWatches,
  updateWeatherWatch,
} from "../../../weather/commercialWeatherApi";
import WeatherWatchSettingsModal from "./WeatherWatchSettingsModal";
import WeatherWatchStatusBadge from "./WeatherWatchStatusBadge";

export default function WeatherWatchButton({
  targetType = "property",
  targetId,
  label,
  latitude,
  longitude,
  clientViewId,
  className = "",
}) {
  const { isAuthenticated } = useAuth();
  const [watch, setWatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    if (!isAuthenticated || !targetId) {
      setWatch(null);
      return;
    }

    setLoading(true);
    try {
      const payload = await fetchWeatherWatches({ targetType, targetId });
      setWatch((payload.watches || []).find((row) => row.enabled) || payload.watches?.[0] || null);
      setErrorMessage("");
    } catch (error) {
      setWatch(null);
      setErrorMessage(error.message || "Unable to load weather watches.");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, targetId, targetType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (form) => {
    setSaving(true);
    setErrorMessage("");
    try {
      const body = {
        targetType,
        targetId: targetId ? String(targetId) : null,
        label: label || form.label,
        latitude,
        longitude,
        clientViewId,
        ...form,
      };

      if (watch) {
        const payload = await updateWeatherWatch(watch.watchId, body);
        setWatch(payload.watch);
      } else {
        const payload = await createWeatherWatch(body);
        setWatch(payload.watch);
      }

      setModalOpen(false);
    } catch (error) {
      setErrorMessage(error.message || "Unable to save weather watch.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!watch) {
      return;
    }
    setSaving(true);
    try {
      await deleteWeatherWatch(watch.watchId);
      setWatch(null);
    } catch (error) {
      setErrorMessage(error.message || "Unable to remove watch.");
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <p className={`muted weather-watch-button ${className}`.trim()}>
        Sign in to watch weather risk for this {targetType}.
      </p>
    );
  }

  return (
    <div className={`weather-watch-button ${className}`.trim()}>
      {watch ? <WeatherWatchStatusBadge active={watch.enabled} /> : null}

      <div className="weather-watch-button__actions">
        <button
          type="button"
          className="weather-btn weather-btn-primary weather-btn--compact"
          disabled={loading || saving}
          onClick={() => setModalOpen(true)}
        >
          {watch ? "Edit weather watch" : "Watch weather"}
        </button>
        {watch ? (
          <button
            type="button"
            className="weather-btn weather-btn-secondary weather-btn--compact"
            disabled={saving}
            onClick={handleRemove}
          >
            Remove
          </button>
        ) : null}
      </div>

      {errorMessage ? <p className="weather-watch-button__error muted">{errorMessage}</p> : null}

      <WeatherWatchSettingsModal
        open={modalOpen}
        initialValues={watch || { riskThreshold: "elevated" }}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
}
